import { observer } from 'mobx-react';
import { FunctionComponent, h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useStore } from '../../../../helpers/use-store';
import { useTranslation } from '../../../../hooks/use-translation';
import MaskedPhoneConfirmationModal from '../masked-phone-confirmation-modal/masked-phone-confirmation-modal';

interface Props {
	onMessageClick: () => void;
}

const callDriver = (phone: string) => {
	const driverPhoneLink = document.createElement('a');
	driverPhoneLink.href = `tel:${phone}`;

	// we need it for making it work with iframe on safari ios, but with iframe on chrome it doesnt work
	if (navigator?.userAgent?.includes('iPhone')) {
		driverPhoneLink.target = '_parent';
	}

	driverPhoneLink.click();
};

const DriverActions: FunctionComponent<Props> = ({ onMessageClick }) => {
	const {
		driverTrackingStore: {
			customerExperience: { employee_phone, hide_call_to_driver, hide_note_to_driver, mask_phone_numbers },
			getMaskedPhone,
			maskPhoneNumber
		}
	} = useStore();
	const { t } = useTranslation();
	const { ariaStore } = useStore();

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const onCallMaskedPhone = () => {
		callDriver(maskPhoneNumber);
	};

	useEffect(() => {
		if (mask_phone_numbers) {
			getMaskedPhoneValue();
		}
	}, [mask_phone_numbers]);

	const getMaskedPhoneValue = async () => {
		try {
			await getMaskedPhone();
		} catch (error) {
			console.error('Failed to get masked phone', error);
		}
	};

	const triggerPhoneCall = useCallback(async () => {
		if (mask_phone_numbers) {
			openModal();
			return;
		}

		callDriver(employee_phone);
	}, [employee_phone, mask_phone_numbers, openModal]);

	const shouldDisplayCall = !hide_call_to_driver && (employee_phone || mask_phone_numbers);

	return (
		<div className="tracking-driver-card-contact-methods" aria-hidden={ariaStore.isModalOpen}>
			{shouldDisplayCall && (
				<div
					className="tracking-driver-card-contact-method"
					onClick={triggerPhoneCall}
					data-testid="tracking-driver-card-contact-method-phone"
					aria-hidden="false"
					tabIndex={1}
					aria-label={`${t('TRACKING.CALL_DRIVER')} ${mask_phone_numbers ? maskPhoneNumber : employee_phone}`}
					role="button"
				>
					<BringgIcon iconName={BringgFontIcons.Phone} />
				</div>
			)}

			{!hide_note_to_driver && (
				<div
					className="tracking-driver-card-contact-method"
					onClick={onMessageClick}
					data-testid="tracking-driver-card-contact-method-message"
					aria-hidden="false"
					tabIndex={2}
					aria-label={t('TRACKING.MESSAGE_DRIVER')}
					role="button"
				>
					<BringgIcon iconName={BringgFontIcons.MessageMail} />
				</div>
			)}

			<MaskedPhoneConfirmationModal isOpen={isModalOpen} onCall={onCallMaskedPhone} onCancel={closeModal} />
		</div>
	);
};

export default observer(DriverActions);
